// Sticker sheets, window clings, table tent, pocket cards and counter strips.
// Ported from HG-CC's `build-activation-pack.mjs`, geometry guards included.

import { qrForSize, type PackQr } from "./qr";
import { A4, brandLockup, brandMark, escapeHtml, font, palette } from "./tokens";
import type { PackKit } from "@/types/activation/packKit";

/** What a built page hands back: markup, its scoped CSS, and its QR reading. */
export interface PackPagePart {
  html: string;
  css: string;
  /** Present when the page carries a code, so the UI can show the module size. */
  qr?: { label: string; sizeMm: number; qr: PackQr };
}

export interface StickerOptions {
  shape: "round" | "square" | "rect";
  sizeMm?: number;
  wMm?: number;
  hMm?: number;
  cols: number;
  rows: number;
  label: string;
  note: string;
  /** 'qr' drops the caption line so the code can run out to the usable area. */
  variant?: "text" | "qr";
}

/** What `stickerArt` hands back: one cell's markup, its scoped CSS, its code. */
export interface StickerArt {
  html: string;
  css: string;
  w: number;
  h: number;
  qr: { label: string; sizeMm: number; qr: PackQr };
}

/**
 * One sticker, at any size or shape.
 *
 * Every sheet draws its cells through here, including the mixed starter sheet.
 * The first version of that sheet hand-rolled its own sizing and silently
 * clipped the business name off every circle on it, which is exactly the
 * failure the guards below exist to prevent. One renderer, one set of guards.
 *
 * Two failure modes have already bitten and both are guarded:
 *   1. The content stack overflowing and silently eating the brand line.
 *   2. Worse: a QR whose top corners get clipped by the circular edge, which
 *      makes the code undecodable while still looking fine at a glance.
 *
 * On a round sticker the only area guaranteed clear of the curve is the square
 * inscribed in the inner circle: side = (diameter - 2*border) / sqrt(2).
 *
 * `sel` is the CSS selector prefix the returned rules hang off, so several cell
 * families can live on one page without colliding.
 */
export async function stickerArt(
  kit: PackKit,
  opts: Omit<StickerOptions, "cols" | "rows" | "note"> & { note?: string },
  sel: string,
): Promise<StickerArt> {
  const { shape, label, variant = "text" } = opts;
  const sizeMm = opts.sizeMm ?? 0;
  const w = opts.wMm || sizeMm;
  const h = opts.hMm || sizeMm;

  const round = shape === "round";
  const rect = shape === "rect";
  // A rectangle only takes the code-beside-text treatment while it is genuinely
  // long and thin. At 100 x 70 that layout leaves a 28mm column for the words
  // and the genie name comes out four lines deep with the business cut to "Gla…".
  // Anything under about twice as wide as it is tall stacks instead.
  const rectStack = rect && w / h < 1.9;
  const rectRow = rect && !rectStack;
  const border = Math.min(w, h) * 0.05;

  let qrMm: number;
  let cellInner = 0;
  if (rectRow) {
    qrMm = h * 0.72;
  } else if (rectStack) {
    qrMm = h * (variant === "qr" ? 0.58 : 0.46);
  } else {
    cellInner = round ? (sizeMm - border * 2) / Math.SQRT2 : sizeMm - sizeMm * 0.09 * 2;
    // Note this ratio is off cellInner, not the diameter. On a circle the
    // diameter is not usable width, and sizing off it is how a code ends up
    // with its finder patterns under the curve.
    qrMm = variant === "qr" ? cellInner * 0.8 : sizeMm * (round ? 0.5 : 0.56);
  }

  // A rectangle label is short and wide. Scaling type off the smaller dimension
  // (the way a square or a circle has to) leaves a 90mm label carrying 2.4mm
  // type with 40mm of empty space beside it, so rect scales off its height.
  const lineMm = rectRow ? h * 0.125 : rectStack ? h * 0.1 : Math.min(w, h) * 0.068;
  const brandMm = rectRow ? h * 0.085 : rectStack ? h * 0.075 : Math.min(w, h) * 0.05;
  const gapA = Math.min(w, h) * 0.035;
  const gapB = Math.min(w, h) * 0.015;

  if (rectStack) {
    const padStack = h * 0.1;
    // The code-led variant carries the name alone at 1.4x, same as a round.
    const stack =
      variant === "qr"
        ? qrMm + gapA + brandMm * 1.15 * 1.4
        : qrMm + gapA + lineMm * 1.15 + gapB + brandMm * 1.15;
    if (stack > h - padStack * 2) {
      throw new Error(
        `Sticker ${label}: stacked content ${stack.toFixed(1)}mm exceeds usable ${(h - padStack * 2).toFixed(1)}mm.`,
      );
    }
  }

  if (!rect) {
    const stack =
      variant === "qr"
        ? qrMm + gapA + brandMm * 1.15
        : round
          ? qrMm + gapA + brandMm * 1.15 * 1.4
          : qrMm + gapA + lineMm * 1.15 + gapB + brandMm * 1.15;
    if (stack > cellInner) {
      throw new Error(
        `Sticker ${label}: content stack ${stack.toFixed(1)}mm exceeds usable ${cellInner.toFixed(1)}mm.`,
      );
    }
    if (round) {
      // The QR is a square offset above centre by half the text block. Its far
      // corners must still clear the circle or the artwork eats the finder
      // patterns and nothing scans.
      const offset = (stack - qrMm) / 2;
      const cornerRadius = Math.hypot(qrMm / 2, qrMm / 2 + offset);
      const clearRadius = sizeMm / 2 - border;
      if (cornerRadius > clearRadius) {
        throw new Error(
          `Sticker ${label}: QR corners reach ${cornerRadius.toFixed(1)}mm from centre but the ` +
            `clear radius is ${clearRadius.toFixed(1)}mm. The circular edge would clip the code.`,
        );
      }
    }
  }

  const q = await qrForSize(kit.url, qrMm, { ink: kit.brand.inkDeep, label });

  const pad = rectRow ? h * 0.12 : rectStack ? h * 0.1 : (Math.min(w, h) - cellInner) / 2;
  const textWidth = rectRow ? w - qrMm - h * 0.36 : rectStack ? w - pad * 2 : cellInner * 0.94;

  const inner = rectRow
    ? `<div class="qr">${q.svg}</div>
       <div class="rtext">
         <div class="line">${escapeHtml(kit.nameIsGenie ? kit.stickerName : kit.genieUseCase)}</div>
         <div class="brand">${escapeHtml(kit.subLine)}</div>
       </div>`
    : rectStack
      ? variant === "qr"
        ? `<div class="qr">${q.svg}</div>
           <div class="brand round">${escapeHtml(kit.stickerName)}</div>`
        : `<div class="qr">${q.svg}</div>
           <div class="line">${escapeHtml(kit.nameIsGenie ? kit.stickerName : kit.genieUseCase)}</div>
           <div class="brand">${escapeHtml(kit.subLine)}</div>`
      : variant === "qr"
        ? `<div class="qr">${q.svg}</div>
           <div class="brand round">${escapeHtml(kit.stickerName)}</div>`
        : round
          ? `<div class="qr">${q.svg}</div>
             <div class="brand round">${escapeHtml(kit.stickerName)}</div>`
          : `<div class="qr">${q.svg}</div>
             <div class="line">${escapeHtml(kit.nameIsGenie ? kit.stickerName : kit.genieUseCase)}</div>
             <div class="brand">${escapeHtml(kit.subLine)}</div>`;

  const radius = round ? "50%" : `${Math.min(w, h) * (rect ? 0.16 : 0.14)}mm`;

  const css = `
    ${sel} { position: absolute; width: ${w}mm; height: ${h}mm; }
    ${sel} .cut { border-radius: ${radius}; }
    ${sel} .art { position: absolute; inset: 0; border-radius: ${radius};
      background: #FFFFFF; border: ${border}mm solid ${palette.primary};
      display: flex; ${rectRow ? "flex-direction: row; gap: " + h * 0.14 + "mm;" : "flex-direction: column;"}
      align-items: center; justify-content: center; padding: ${pad}mm; overflow: hidden; }
    ${sel} .qr { flex: 0 0 auto; }
    ${sel} .qr svg { width: ${qrMm}mm; height: ${qrMm}mm; }
    ${sel} .rtext { flex: 1 1 auto; min-width: 0; text-align: left; }
    ${sel} .line { flex: 0 0 auto; font-size: ${lineMm}mm; font-weight: 700;
      color: ${palette.ink}; ${rectRow ? "" : `margin-top: ${gapA}mm; text-align: center;`}
      line-height: 1.15; max-width: ${textWidth}mm; }
    ${sel} .brand { flex: 0 0 auto; font-size: ${brandMm}mm; font-weight: 500;
      color: ${palette.mutedFg}; margin-top: ${gapB}mm; ${rectRow ? "" : "text-align: center;"}
      line-height: 1.15; max-width: ${textWidth}mm;
      overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
    /* Round carries the name alone, so it takes the weight the prompt line
       would have had on a square. */
    ${sel} .brand.round { font-size: ${brandMm * 1.4}mm; font-weight: 700;
      color: ${palette.ink}; margin-top: ${gapA}mm; white-space: normal; }
  `;

  return {
    html: `<div class="art">${inner}</div><div class="cut"></div>`,
    css,
    w,
    h,
    qr: { label, sizeMm: qrMm, qr: q },
  };
}

/** A full A4 sheet of one sticker, multi up, with cut guides on the trim path. */
export async function stickerPage(
  kit: PackKit,
  opts: StickerOptions,
  n: number,
): Promise<PackPagePart> {
  const { cols, rows, label, note } = opts;
  const sizeMm = opts.sizeMm ?? 0;
  const w = opts.wMm || sizeMm;
  const h = opts.hMm || sizeMm;
  const gutterX = (A4.w - cols * w) / (cols + 1);
  const gutterY = (A4.h - 14 - rows * h) / (rows + 1); // 14mm reserved for the footer note
  if (gutterX < 3 || gutterY < 3) {
    throw new Error(
      `Sticker ${label}: ${cols}x${rows} at ${w}x${h}mm leaves ${gutterX.toFixed(1)}x${gutterY.toFixed(1)}mm gutters. Too tight to cut.`,
    );
  }

  const art = await stickerArt(kit, opts, `#p${n} .cell`);

  const cells: string[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = gutterX + c * (w + gutterX);
      const y = gutterY + r * (h + gutterY);
      cells.push(`<div class="cell" style="left:${x}mm;top:${y}mm">${art.html}</div>`);
    }
  }

  const html = `<div class="sheet" id="p${n}">
    ${cells.join("")}
    <div class="sheetnote">${escapeHtml(note)}</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css: art.css, qr: art.qr };
}

/**
 * Big circles for glass. One 150mm and one 90mm on the same sheet, because a
 * business puts the big one on the main door and the small one on a side
 * window. Ratios are bounded by the inscribed square of the clear circle, same
 * constraint as a round sticker.
 */
export async function clingPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const big = 150;
  const small = 90;
  const qBig = await qrForSize(kit.url, big * 0.52, { ink: kit.brand.inkDeep, label: "cling-150" });
  const qSmall = await qrForSize(kit.url, small * 0.5, { ink: kit.brand.inkDeep, label: "cling-90" });

  const disc = (d: number, q: PackQr, headline: string, sub: string, cls: string) => `
    <div class="disc ${cls}" style="width:${d}mm;height:${d}mm">
      <div class="art">
        <div class="eyebrow">${escapeHtml(headline)}</div>
        <div class="qrbox">${q.svg}</div>
        <div class="who">${escapeHtml(sub)}</div>
      </div>
      <div class="cut"></div>
    </div>`;

  const css = `
    #p${n} { padding: 14mm 0 0; }
    #p${n} .discs { display: flex; flex-direction: column; align-items: center; gap: 12mm; }
    #p${n} .disc { position: relative; }
    #p${n} .disc .cut { border-radius: 50%; }
    #p${n} .art { position: absolute; inset: 0; border-radius: 50%;
      background: #FFFFFF; border: 2.4mm solid ${palette.primary};
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; }
    #p${n} .eyebrow { font-size: 4.6mm; font-weight: 700; letter-spacing: 0.22em;
      text-transform: uppercase; color: ${palette.secondary}; margin-bottom: 3mm; }
    #p${n} .who { font-size: 6.2mm; font-weight: 700; color: ${palette.ink};
      margin-top: 3.5mm; max-width: 78%; line-height: 1.2; }
    #p${n} .big .qrbox svg { width: ${big * 0.52}mm; height: ${big * 0.52}mm; }
    #p${n} .small .qrbox svg { width: ${small * 0.5}mm; height: ${small * 0.5}mm; }
    #p${n} .small .eyebrow { font-size: 2.6mm; margin-bottom: 2mm; }
    #p${n} .small .who { font-size: 3.2mm; margin-top: 2.4mm; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="discs">
      ${disc(big, qBig, kit.stickerName, kit.subLine, "big")}
      ${disc(small, qSmall, kit.stickerName, kit.subLine, "small")}
    </div>
    <div class="sheetnote">Window clings. Print on static cling film or white vinyl, cut on the dashed circle. Face the glass out, mount at eye height.</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css, qr: { label: "window cling, 150mm", sizeMm: big * 0.52, qr: qBig } };
}

/**
 * A matched pair of 120mm discs, for a business with two doors. Same artwork
 * twice rather than one big and one small.
 */
export async function clingPairPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const d = 120;
  const q = await qrForSize(kit.url, d * 0.5, { ink: kit.brand.inkDeep, label: "cling-120" });

  const disc = `
    <div class="disc" style="width:${d}mm;height:${d}mm">
      <div class="art">
        <div class="eyebrow">${escapeHtml(kit.stickerName)}</div>
        <div class="qrbox">${q.svg}</div>
        <div class="who">${escapeHtml(kit.subLine)}</div>
      </div>
      <div class="cut"></div>
    </div>`;

  const css = `
    #p${n} { padding: 12mm 0 0; }
    #p${n} .discs { display: flex; flex-direction: column; align-items: center; gap: 14mm; }
    #p${n} .disc { position: relative; }
    #p${n} .disc .cut { border-radius: 50%; }
    #p${n} .art { position: absolute; inset: 0; border-radius: 50%;
      background: #FFFFFF; border: 2.2mm solid ${palette.primary};
      display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
    #p${n} .eyebrow { font-size: 3.6mm; font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: ${palette.secondary}; margin-bottom: 2.6mm; }
    #p${n} .qrbox svg { width: ${d * 0.5}mm; height: ${d * 0.5}mm; }
    #p${n} .who { font-size: 5mm; font-weight: 700; color: ${palette.ink};
      margin-top: 3mm; max-width: 78%; line-height: 1.2; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="discs">${disc}${disc}</div>
    <div class="sheetnote">Matched window clings, 120mm, 2 up. For a business with two doors, or a door and a counter front. Static cling film, cut on the dashed circle.</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css, qr: { label: "window cling, 120mm", sizeMm: d * 0.5, qr: q } };
}

/**
 * One sheet with a bit of everything, for the first walk around the building.
 * Print this one first, stick them up, then come back for a full sheet of
 * whatever size actually got used.
 */
export async function mixedPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const fam = {
    sq: await stickerArt(kit, { shape: "square", sizeMm: 75, label: "mixed-square-75" }, `#p${n} .fam-sq`),
    rd: await stickerArt(kit, { shape: "round", sizeMm: 60, variant: "qr", label: "mixed-round-60" }, `#p${n} .fam-rd`),
    lb: await stickerArt(kit, { shape: "rect", wMm: 90, hMm: 35, label: "mixed-label-90x35" }, `#p${n} .fam-lb`),
  };

  // Two 75mm squares across the top, six 60mm rounds under them, two 90 x 35mm
  // labels at the foot. Positions are explicit because the sizes do not share a
  // grid. The last row ends at 271mm, clear of the note at the bottom edge.
  const at = (k: keyof typeof fam, x: number, y: number) =>
    `<div class="cell fam-${k}" style="left:${x}mm;top:${y}mm">${fam[k].html}</div>`;
  const cells = [
    at("sq", 22, 14), at("sq", 113, 14),
    at("rd", 15, 99), at("rd", 75, 99), at("rd", 135, 99),
    at("rd", 15, 164), at("rd", 75, 164), at("rd", 135, 164),
    at("lb", 10, 236), at("lb", 110, 236),
  ].join("");

  const css = `
    #p${n} .cell { position: absolute; }
    ${fam.sq.css}
    ${fam.rd.css}
    ${fam.lb.css}
  `;

  const html = `<div class="sheet" id="p${n}">
    ${cells}
    <div class="sheetnote">Starter sheet. Two 75mm squares, six 60mm rounds and two 90 x 35mm labels. Print this one first, walk the building, put them up, then come back for a full sheet of whatever size you actually used.</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css, qr: fam.sq.qr };
}

/**
 * Everything that goes wrong with a sticker goes wrong in the ten seconds it is
 * being applied. One page, no marketing voice. Where each size goes at this
 * business is on the contents page, so this one stays on method alone.
 */
export function applicationPage(kit: PackKit, n: number): PackPagePart {
  const css = `
    #p${n} { padding: 16mm 18mm 14mm; display: flex; flex-direction: column; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: ${A4.w}mm; height: 3.5mm; background: ${palette.primary}; }
    #p${n} .lockup { margin-bottom: 8mm; }
    #p${n} h1 { font-family: ${font.display}; font-size: 11mm; font-weight: 800; color: ${palette.ink}; margin: 0 0 2mm; }
    #p${n} .lede { font-size: 3.9mm; color: ${palette.mutedFg}; margin: 0 0 7mm; max-width: 165mm; line-height: 1.4; }
    #p${n} h2 { font-family: ${font.display}; font-size: 5.4mm; font-weight: 700; color: ${palette.ink}; margin: 0 0 3mm; }
    #p${n} .steps { list-style: none; margin: 0 0 7mm; padding: 0; }
    #p${n} .steps li { display: flex; gap: 5mm; align-items: flex-start; margin-bottom: 4mm; }
    #p${n} .steps .num { flex: 0 0 auto; width: 8mm; height: 8mm; border-radius: 50%;
      background: ${palette.primary}; color: #FFFFFF; font-family: ${font.display};
      font-weight: 800; font-size: 4.4mm; display: flex; align-items: center; justify-content: center; }
    #p${n} .steps b { display: block; font-size: 4mm; color: ${palette.ink}; margin-bottom: 1mm; }
    #p${n} .steps span.body { display: block; font-size: 3.4mm; color: ${palette.mutedFg}; line-height: 1.35; }
    #p${n} .cols { display: flex; gap: 7mm; margin-bottom: 7mm; }
    #p${n} .cols > div { flex: 1; background: ${palette.pageTint}; border: 0.3mm solid ${palette.border};
      border-radius: 3mm; padding: 5mm; }
    #p${n} .cols ul { list-style: none; margin: 0; padding: 0; }
    #p${n} .cols li { font-size: 3.3mm; color: ${palette.mutedFg}; line-height: 1.35; padding: 1.2mm 0 1.2mm 5mm; position: relative; }
    #p${n} .cols li:before { content: ''; position: absolute; left: 0; top: 2.4mm;
      width: 2mm; height: 2mm; border-radius: 50%; background: ${palette.primary}; }
    #p${n} .cols.dont li:before { background: ${palette.secondary}; }
    #p${n} .warn { margin-top: auto; background: ${palette.pageTint};
      border-left: 1.4mm solid ${palette.secondary}; border-radius: 0 3mm 3mm 0; padding: 5mm 6mm; }
    #p${n} .warn b { display: block; font-size: 3.9mm; color: ${palette.ink}; margin-bottom: 1.6mm; }
    #p${n} .warn p { margin: 0; font-size: 3.3mm; color: ${palette.mutedFg}; line-height: 1.4; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    ${brandLockup(kit.brand, 11, "light")}
    <h1>Putting them up</h1>
    <p class="lede">Everything that goes wrong with a sticker goes wrong in the ten seconds it is
      being applied. This is the whole method.</p>

    <ol class="steps">
      <li><span class="num">1</span><span><b>Clean and dry the surface</b>
        <span class="body">Warm soapy water, then dry it properly. Dust and grease are what make a sticker lift at one corner two weeks later.</span></span></li>
      <li><span class="num">2</span><span><b>Peel from one edge, not the middle</b>
        <span class="body">Lay the leading edge down first, then sweep across with a thumb or a card. That pushes the air out ahead of you instead of trapping it.</span></span></li>
      <li><span class="num">3</span><span><b>Press the whole face, hard</b>
        <span class="body">Especially the edges. Thirty seconds of pressure now is the difference between a season and a week.</span></span></li>
      <li><span class="num">4</span><span><b>Scan it before you walk away</b>
        <span class="body">From about arm's length, the way a customer will. If it does not read, move it: glare and curved surfaces are the usual culprits.</span></span></li>
    </ol>

    <div class="cols">
      <div><h2>Do</h2><ul>
        <li>Put them at hand or eye height, where a phone can get square to them</li>
        <li>Choose flat over curved every time</li>
        <li>Leave a clear border of surface around the sticker</li>
        <li>Use matte laminate anywhere sunlight hits</li>
      </ul></div>
      <div class="dont"><h2>Do not</h2><ul>
        <li>Apply below about 10 degrees, the adhesive will not grab</li>
        <li>Put a code behind glass with a reflection on it</li>
        <li>Stretch one around a tight curve, it distorts the code</li>
        <li>Cover any part of it, even the white border</li>
      </ul></div>
    </div>

    <div class="warn">
      <b>One rule above all the others</b>
      <p>Scan every code from a proof before you order a run, and scan the first one of each batch
        after you stick it down. A sticker nobody can scan is worse than no sticker, because the
        customer decides the whole thing does not work.</p>
    </div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css };
}

/**
 * A4 portrait folded once across the middle. The top panel is rotated 180 so
 * both faces read upright once it stands on a counter.
 */
export async function tentPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const panelH = A4.h / 2;
  const q = await qrForSize(kit.url, 52, { ink: kit.brand.inkDeep, label: "table-tent" });

  const panel = `
    <div class="pinner">
      ${brandLockup(kit.brand, 10, "dark")}
      <h2>Ask us anything.</h2>
      <div class="row">
        <div class="qrcard">${q.svg}</div>
        <div class="copy">
          <p class="cta">${escapeHtml(kit.genieUseCase)}</p>
          <ul class="helps">
            ${kit.helps.slice(0, 3).map((h) => `<li>${escapeHtml(h)}</li>`).join("")}
          </ul>
          ${kit.footerLine ? `<p class="url">${escapeHtml(kit.footerLine)}</p>` : ""}
        </div>
      </div>
    </div>`;

  const css = `
    #p${n} .panel { position: absolute; left: 0; width: ${A4.w}mm; height: ${panelH}mm;
      background: ${palette.inkDeep}; color: #FFFFFF; overflow: hidden; }
    #p${n} .panel.top { top: 0; transform: rotate(180deg); }
    #p${n} .panel.bottom { top: ${panelH}mm; }
    /* Content sits slightly above centre: once folded, that is eye height on
       the standing face rather than down near the table. */
    #p${n} .pinner { position: absolute; inset: 0; padding: 16mm 16mm 22mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
    #p${n} .pinner .lockup { margin-bottom: 6mm; }
    #p${n} h2 { font-family: ${font.display}; font-size: 11mm; font-weight: 800; margin: 0 0 7mm; line-height: 1.05; }
    #p${n} .row { display: flex; align-items: center; gap: 9mm; text-align: left; }
    #p${n} .qrcard { background: #FFFFFF; border-radius: 3.5mm; padding: 4.5mm; }
    #p${n} .qrcard svg { width: 52mm; height: 52mm; }
    #p${n} .copy { max-width: 78mm; }
    #p${n} .cta { font-family: ${font.display}; font-weight: 700; font-size: 6.2mm; color: ${palette.highlight}; margin: 0 0 3mm; line-height: 1.15; }
    #p${n} .q { font-size: 4.1mm; font-style: italic; color: rgba(255,255,255,0.82); margin: 0 0 4mm; }
    #p${n} .url { font-size: 4.4mm; font-weight: 600; margin: 0; }
    #p${n} .fold { position: absolute; left: 0; top: ${panelH}mm; width: ${A4.w}mm; height: 0;
      border-top: 0.25mm dashed rgba(255,255,255,0.5); }
    #p${n} .foldlabel { position: absolute; left: 5mm; top: ${panelH - 3.4}mm;
      font-size: 2.7mm; color: rgba(255,255,255,0.6); letter-spacing: 0.1em; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="panel top">${panel}</div>
    <div class="panel bottom">${panel}</div>
    <div class="fold"></div>
    <div class="foldlabel">FOLD HERE</div>
    <div class="pagenum oncolour">${n}</div>
  </div>`;

  return { html, css, qr: { label: "table tent", sizeMm: 52, qr: q } };
}

export async function cardsPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const cardW = 90;
  const cardH = 55;
  const cols = 2;
  const rows = 4;
  const gutterX = (A4.w - cols * cardW) / (cols + 1);
  const gutterY = (A4.h - 14 - rows * cardH) / (rows + 1);
  const q = await qrForSize(kit.url, 36, { ink: kit.brand.inkDeep, label: "cards" });

  // Some kits carry a domain as the footer and some carry the business name.
  // Printing both means the card says the name twice.
  const secondLine =
    kit.footerLine ? `<div class="url">${escapeHtml(kit.footerLine)}</div>` : "";

  const cells: string[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = gutterX + c * (cardW + gutterX);
      const y = gutterY + r * (cardH + gutterY);
      cells.push(`<div class="card" style="left:${x}mm;top:${y}mm">
        <div class="left">
          ${brandMark(kit.brand, 7)}
          <div class="cta">${escapeHtml(kit.genieUseCase)}</div>
          <div class="who">${escapeHtml(kit.subLine)}</div>
          ${secondLine}
        </div>
        <div class="qr">${q.svg}</div>
        <div class="cut oncolour"></div>
      </div>`);
    }
  }

  const css = `
    #p${n} .card { position: absolute; width: ${cardW}mm; height: ${cardH}mm;
      background: ${palette.inkDeep}; color: #FFFFFF; border-radius: 2mm;
      display: flex; align-items: center; gap: 4mm; padding: 6mm; overflow: hidden; }
    #p${n} .card .cut { border-radius: 2mm; }
    #p${n} .left { flex: 1 1 auto; min-width: 0; }
    #p${n} .brandmark { margin-bottom: 3mm; }
    #p${n} .cta { font-family: ${font.display}; font-weight: 700; font-size: 4.8mm;
      line-height: 1.1; color: ${palette.highlight}; margin-bottom: 2mm; }
    #p${n} .who { font-size: 3.4mm; font-weight: 600; color: rgba(255,255,255,0.9);
      margin-bottom: 1mm; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
    #p${n} .url { font-size: 3.1mm; color: rgba(255,255,255,0.65); }
    #p${n} .qr { flex: 0 0 auto; background: #FFFFFF; border-radius: 1.5mm; padding: 2.5mm; }
    #p${n} .qr svg { width: 36mm; height: 36mm; }
  `;

  const html = `<div class="sheet" id="p${n}">
    ${cells.join("")}
    <div class="sheetnote">Pocket cards, 90 x 55mm, 8 up. Print on 300gsm or heavier, trim on the dashed guide. Leave a stack by the till.</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css, qr: { label: "pocket card", sizeMm: 36, qr: q } };
}

/** Long thin strips for a counter edge, a shelf lip, or a door push plate. */
export async function stripsPage(kit: PackKit, n: number): Promise<PackPagePart> {
  const stripW = 186;
  const stripH = 38;
  const rows = 6;
  const x = (A4.w - stripW) / 2;
  const gutterY = (A4.h - 14 - rows * stripH) / (rows + 1);
  const q = await qrForSize(kit.url, 28, { ink: kit.brand.inkDeep, label: "strips" });

  const cells: string[] = [];
  for (let r = 0; r < rows; r++) {
    const y = gutterY + r * (stripH + gutterY);
    cells.push(`<div class="strip" style="top:${y}mm">
      <div class="qr">${q.svg}</div>
      <div class="copy">
        <div class="cta">${escapeHtml(kit.genieUseCase)}</div>
        <div class="who">${escapeHtml(kit.subLine)}</div>
      </div>
      ${brandMark(kit.brand, 11)}
      <div class="cut oncolour"></div>
    </div>`);
  }

  const css = `
    #p${n} .strip { position: absolute; left: ${x}mm; width: ${stripW}mm; height: ${stripH}mm;
      background: ${palette.inkDeep}; color: #FFFFFF; border-radius: 2mm;
      display: flex; align-items: center; gap: 5mm; padding: 4mm 6mm; overflow: hidden; }
    #p${n} .strip .cut { border-radius: 2mm; }
    #p${n} .qr { flex: 0 0 auto; background: #FFFFFF; border-radius: 1.2mm; padding: 1.6mm; }
    #p${n} .qr svg { width: 28mm; height: 28mm; }
    #p${n} .copy { flex: 1 1 auto; min-width: 0; }
    #p${n} .cta { font-family: ${font.display}; font-weight: 700; font-size: 5.4mm;
      color: ${palette.highlight}; line-height: 1.1; margin-bottom: 1.6mm; }
    #p${n} .who { font-size: 3.3mm; color: rgba(255,255,255,0.78); line-height: 1.25;
      overflow: hidden; }
    #p${n} .brandmark { flex: 0 0 auto; }
  `;

  const html = `<div class="sheet" id="p${n}">
    ${cells.join("")}
    <div class="sheetnote">Counter and shelf strips, 186 x 38mm, 6 up. Trim on the dashed guide, fix along a counter edge, a shelf lip or a door push plate.</div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css, qr: { label: "counter strip", sizeMm: 28, qr: q } };
}
